import React from "react";
import { useTranslation } from "react-i18next";

import { Product, ProductId, ProductKey } from "../../../../commons/specs/product";
import { Brand } from "../../../../commons/types/brand";
import type { GetPrintItem } from "../../../../commons/types/print-mail";
import { ToolKey } from "../../../../commons/types/tool";
import * as icons from "../../../../resources/icons";
import Icon from "../../../commons/components/Icon/Icon";
import IconLink from "../../../commons/components/IconLink/IconLink";
import TabBarOverlay from "../../../commons/components/TabBarOverlay/TabBarOverlay";
import { useTrackingContext } from "../../../commons/container/utils/tracking-context";
import { ROUTES } from "../../../commons/routes";
import { generatePath, useAppHistory, useAppLocation } from "../../libs/hooks/use-app-routing";
import { useMediaQuery } from "../../libs/hooks/use-media-query";
import useOnMount from "../../libs/hooks/use-on-mount";
import { useBrands } from "../../libs/queries/use-brands";
import { useSettings } from "../../libs/queries/use-settings";
import { useWawiConnectionInfo } from "../../libs/queries/use-wawi-connection";
import ProductTitlePartial from "../partials/ProductTitlePartial";
import { useVeloconnectEndpointContext } from "../utils/veloconnect-endpoint-context";

import VariantsContentPartial from "./VariantsContentPartial";

interface Props {
  variantId?: ProductId;
  product: Product;
  getPrintItem?: GetPrintItem;
  brand: Brand;
  hasInternetConnectivity: boolean;
  modal?: React.ReactNode;
}

const AvailabilityOverlay = ({ variantId, product, getPrintItem, brand, hasInternetConnectivity, modal }: Props) => {
  const { t } = useTranslation(["commons"]);
  const history = useAppHistory();
  const location = useAppLocation();

  const veloconnectEndpoint = useVeloconnectEndpointContext();

  const breakpoints = useMediaQuery();
  const { mixpanel } = useTrackingContext();

  const brands = useBrands();
  const settings = useSettings();

  const wawiConnectionInfo = useWawiConnectionInfo();

  const isMobile = !breakpoints.l;

  const isAvailabilityActive = brands.getBrand(brand.key)?.availabilityPossible ?? false;

  const isVeloconnectActive = !veloconnectEndpoint.query.isFetching && !!veloconnectEndpoint?.query.data?.isConfigured;
  const shouldShowVeloconnectNotification =
    !veloconnectEndpoint.query.isFetching && veloconnectEndpoint.shouldShowNotification;
  const isWawiActive = isAvailabilityActive && wawiConnectionInfo.isWawiConfigured;

  useOnMount(() => {
    mixpanel?.trackProductAvailabilityViewed(product, isWawiActive, isVeloconnectActive, variantId);
  });

  const renderIconLinkLabel = () => {
    const renderLabel = () => {
      const shouldRenderAvailabilityHeadline =
        (isAvailabilityActive && shouldShowVeloconnectNotification) || isVeloconnectActive || isWawiActive;

      const mainHeadline = shouldRenderAvailabilityHeadline
        ? t("commons:availabilityOverlayProductDetails.variantsAvailabilityHeadline")
        : t("commons:availabilityOverlayProductDetails.variantsHeadline");

      return t("commons:availabilityOverlayProductDetails.closeHeadlineButton", { headline: mainHeadline });
    };

    if (isMobile) {
      return t("commons:availabilityOverlayProductDetails.closeButton");
      // We have to wait until the loading state of the Veloconnect Proxy is finished, to prevent a premature rendering of the text.
    } else if (veloconnectEndpoint.query.isFetching) {
      return undefined;
    } else {
      return renderLabel();
    }
  };

  return (
    <TabBarOverlay
      noContentPadding
      headerLeft={
        <IconLink
          size={isMobile ? "s" : "base"}
          href={generatePath(ROUTES.PRODUCT_DETAILS.INDEX, { id: product[ProductKey.ProductId] })}
          icon={<Icon source={icons.IconSmallAngleDown} />}
          onClick={(_event: React.MouseEvent<HTMLElement>, href: string) => {
            history.replace(href, location.state);
          }}
          classNames={["u-extended-click-area"]}
        >
          {renderIconLinkLabel()}
        </IconLink>
      }
      headerCenter={
        <ProductTitlePartial
          product={product}
          currency={settings.initialized.currency}
          customization={settings.initialized.customization}
          assortmentPriceSettings={settings.assortmentPriceSettings}
        />
      }
    >
      {modal}
      <VariantsContentPartial
        variantId={variantId}
        product={product}
        getPrintItem={getPrintItem}
        hasInternetConnectivity={hasInternetConnectivity}
        openFrameSizeCalculatorTool={() => {
          history.push(generatePath(ROUTES.TOOL.INDEX, { key: ToolKey.BikeFrameSizeCalculator }));
        }}
      />
    </TabBarOverlay>
  );
};

export default AvailabilityOverlay;
